import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import { ButtonLoader } from "Components/ButtonLoader";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { CREATE_ADMIN_ACTION_URL, UPDATE_ADMIN_ACTION_URL } from "Constant/api";
import { authService } from "Service/Axios";
import { Validator } from "Utils/validator";
import { COLOR_SETTINGS } from "Constant/constants";

export const CreateAction = ({
  show,
  toggle,
  selectedData,
  fetchList,
  projectList,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    global: "global",
    projectIds: "",
    actionBackgroundColor: "",
  });
  const [errMsg, setErrMsg] = useState({
    projectIds: "",
    actionName: "",
    actionBackgroundColor: "",
  });

  const {
    global,
    projectIds,
    actionName,
    actionBackgroundColor,
  } = formData;

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
      if (e.target.name === "actionName") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            actionName: t(
              "common.messages.error.adminSettings.actionName.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            actionName: "",
          }));
        }
      }

      if (e.target.name === "actionBackgroundColor") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            actionBackgroundColor: t(
              "common.messages.error.adminSettings.actionBackgroundColor.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            actionBackgroundColor: "",
          }));
        }
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));

    if (name === "projectIds") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: t("common.messages.error.adminSettings.projectIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: "",
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedData) {
      setFormData({
        global: selectedData.global ? "global" : "project",
        projectIds: selectedData.projects?.map((project) => ({
          label: project.name,
          value: project.id,
        })),
        actionName: selectedData.name,
        actionBackgroundColor: selectedData.backgroundColor,
      });
    }
  }, [selectedData, projectList]);

  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(actionName) &&
        Validator.text(actionBackgroundColor)
      ) {
        if (global !== "global") {
          if (Validator.hasMultiple(projectIds)) {
            resolve(true);
          } else {
            setErrMsg((prev) => ({
              ...prev,
              projectIds: t(
                "common.messages.error.adminSettings.projectIds.blank"
              ),
            }));
            resolve(false);
          }
        } else {
          resolve(true);
        }
      } else {
        if (!Validator.text(actionName)) {
          setErrMsg((prev) => ({
            ...prev,
            actionName: t(
              "common.messages.error.adminSettings.actionName.blank"
            ),
          }));
        }
        if (!Validator.text(actionBackgroundColor)) {
          setErrMsg((prev) => ({
            ...prev,
            actionBackgroundColor: t(
              "common.messages.error.adminSettings.actionBackgroundColor.blank"
            ),
          }));
        }
        resolve(false);
      }
    });
  };
  // On Submit
  const onSaveAction = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          global: global === "global",
          name: actionName,
          label: actionName,
          backgroundColor: actionBackgroundColor,
        };
        if (global === "project") {
          payload.projects = projectIds?.map((option) => option.value);
        }
        if (selectedData) {
          payload.gid = selectedData.gid;
          const response = await authService(
            UPDATE_ADMIN_ACTION_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.updateAction")
            );
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await authService(
            CREATE_ADMIN_ACTION_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.createAction")
            );
          } else {
            toast.error(response.message);
          }
        }
        setLoading(false);
        fetchList(projectIds);
        toggle();
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.adminSettings.createAction")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <div className="mb-1">
              <label htmlFor="global" className="form-label me-3">
                {t("common.label.global")}
              </label>

              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  id="global"
                  value="global"
                  checked={global === "global"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.yes")}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <label htmlFor="global" className="form-check-label"></label>
                <input
                  type="radio"
                  id="global"
                  value="project"
                  checked={global === "project"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.no")}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={`mb-3 ${errMsg.projectIds ? "error" : ""}`}>
          <label htmlFor="projectIds" className="form-label">
            {t("common.label.projects")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.projects")}
            options={projectList}
            value={projectIds}
            onChange={(selected) =>
              handleChangeMultiple(selected, "projectIds")
            }
            disabled={global === "global"}
          />
          {errMsg.projectIds && <p>{errMsg.projectIds}</p>}
        </div>
        <div className={`mb-3 ${errMsg.actionName ? "error" : ""}`}>
          <label htmlFor="actionName" className="form-label">
            {t("common.label.actionName")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.actionName")}
            id="actionName"
            name="actionName"
            value={actionName}
            onChange={handleChange}
          />
          {errMsg.actionName && <p>{errMsg.actionName}</p>}
        </div>
        <div className="row">
          <div className="col-sm-6 col-12">
            <label htmlFor="actionBackgroundColor" className="form-label">
              {t("common.label.backgroundColor")}
            </label>
            <ul className="color-list">
              {COLOR_SETTINGS.map((color) => (
                <li className="round" key={color}>
                  <input
                    type="radio"
                    id={`${color}actionBackgroundColor`}
                    value={color}
                    checked={actionBackgroundColor === color}
                    name="actionBackgroundColor"
                    onChange={handleChange}
                  />
                  <label
                    htmlFor={`${color}actionBackgroundColor`}
                    style={{
                      backgroundColor: color,
                      borderColor: color,
                    }}
                  ></label>
                </li>
              ))}
            </ul>
            {errMsg.actionBackgroundColor && (
              <p className="error">{errMsg.actionBackgroundColor}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveAction}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
